// Batch Invoicing — recurring/scheduled invoicing to a group of customers off ONE template
// (Andre: "batch invoicing which can be like v2"). v2 flow (verified against v2.11.25 source):
// LIST of templates → template EDITOR (details + members + per-member line items) → RUN screen
// (pick a date, exclude members, add ad-hoc lines, execute) → completion summary.
//
// NEW vs v2 (this pass, per the 2026-09-02 meeting):
//   1. Per-line RECURRENCE: kind 'recurring' (optional effective_from/effective_to period; both
//      blank = Indefinite) vs 'once_off' (fires the next run only, then retires — shown greyed
//      with "billed in run #N").
//   2. FRIENDLY TOKENS: notes support [MONTH]/[YEAR] (v2 parity — v2's notes helper on
//      app-dev.sleekacc.co.za/batch-invoicing reads exactly "Supports tokens: [MONTH], [YEAR]")
//      via a self-documenting "Insert a token" dropdown above the textarea + a live preview line
//      resolved against today (editor) or the chosen invoice date (run), via the preview endpoint
//      when available, else resolved client-side (resolveTokens, pure + exported for tests).
//      [MONTHA] (short month, e.g. Sep) is NOT a v2 token: it is no longer offered in the picker,
//      but is still resolved so any note that already contains it keeps working.
//
// CSS: aboo.css already carries a full `.bt-*`/`.qi-batch*` block ("Batch Invoicing (§9 A6b)")
// ported from the v2 sweep, unused until this screen existed — this file targets those class
// names directly (bt-card/bt-member/bt-line/bt-run-*/bt-complete-*/…) so almost no new CSS is
// needed; the handful of genuinely-new rules (tokens/period/retired-line) are listed in the
// final report, not added here (CSS is out of scope for this file).
//
// tina4 gotchas honoured (see CLAUDE.md):
//   - Nested ${() => …} blocks APPEND on re-render. Every view below is built from a small set of
//     slots interpolated DIRECTLY inside the one `content` template each screen builds once —
//     never a bare-function slot referenced from INSIDE another bare-function slot's own markup.
//     A slot that must react to a keystroke-heavy signal (fNotes, fNotesOverride) never reads
//     that signal's `.value` in its own JS body — the signal is passed bare to `.value=` so the
//     element updates without the surrounding slot re-rendering (documents_editor's headerGrid
//     pattern); a slot that MUST show live-derived text from that signal (the token preview) is
//     its OWN separate top-level slot instead.
//   - Line-grid keystrokes MUTATE the plain line object in place (no signal write → no re-render,
//     focus survives); structural changes (kind toggle, period date, add/remove line, item pick)
//     reassign `members.value` to force a rebuild — exactly document_editor.js's rule, retargeted
//     per-member since batch lines live inside member objects, not one flat array.
//   - Contact/item search are imperative (openDrop/closeDrop below, ported from document_editor.js
//     — a fresh DOM subtree is cloned per reactive re-render in this tina4js build, so wiring a
//     signal to a search box drops focus on every keystroke).
//
// API CONTRACT — being built in parallel; every path isolated in `ep` below (ONE spot to fix on
// integration). Response/body shapes not spelled out in the brief are my best-effort assumption,
// each flagged inline and summarised in the final report (e.g. GET /{tid} → {template, members:
// [{..., lines:[...]}]}; list rows carry name/notes/active/member_count/last_run_at).
import { html, signal, navigate } from '../shared/t4.js';
import { icon } from '../shared/icons.js';
import { api } from '../shared/api.js';
import { tenantShell, companyName } from './tshell.js';
import { fmtMoney, formatDate, baseCurrencySymbol } from './bank_helpers.js';
import { configureSleekDate } from '../shared/sleekdate.js';
import { t } from '../shared/i18n.js';
import { dropdownKeydown, highlightFirst } from '../shared/dropnav.js';
import { rowDelete } from '../shared/confirm.js';
import { searchSelect } from '../shared/searchselect.js';
import { backChip } from '../shared/vtnav.js';
import { pager } from '../shared/pager.js';

// ── endpoints — the ONE spot to fix when the real API lands (see file header) ─────────────────
const ep = {
  reference:    (cid)           => `/companies/${cid}/documents-reference`,
  tags:         (cid)           => `/companies/${cid}/tags`,
  list:         (cid)           => `/companies/${cid}/batch-templates`,
  create:       (cid)           => `/companies/${cid}/batch-templates`,
  get:          (cid, tid)      => `/companies/${cid}/batch-templates/${tid}`,
  update:       (cid, tid)      => `/companies/${cid}/batch-templates/${tid}`,
  remove:       (cid, tid)      => `/companies/${cid}/batch-templates/${tid}/delete`,
  addMember:    (cid, tid)      => `/companies/${cid}/batch-templates/${tid}/members`,
  removeMember: (cid, tid, mid) => `/companies/${cid}/batch-templates/${tid}/members/${mid}/remove`,
  pauseMember:  (cid, tid, mid) => `/companies/${cid}/batch-templates/${tid}/members/${mid}/pause`,
  addLine:      (cid, mid)      => `/companies/${cid}/batch-members/${mid}/lines`,
  updateLine:   (cid, lid)      => `/companies/${cid}/batch-lines/${lid}`,
  removeLine:   (cid, lid)      => `/companies/${cid}/batch-lines/${lid}/delete`,
  preview:      (cid, tid)      => `/companies/${cid}/batch-templates/${tid}/preview`,
  run:          (cid, tid)      => `/companies/${cid}/batch-templates/${tid}/run`,
  runDetail:    (cid, rid)      => `/companies/${cid}/batch-runs/${rid}`,
  runs:         (cid, tid)      => `/companies/${cid}/batch-templates/${tid}/runs`,
  runCsv:       (cid, rid)      => `/companies/${cid}/batch-runs/${rid}/debit-order.csv`,   // global bank columns + skipped flags
  runPdf:       (cid, rid)      => `/companies/${cid}/batch-runs/${rid}/invoices.pdf`,       // all invoices merged into one PDF
};

// ── pure helpers (exported for unit tests) ──────────────────────────────────────────────────
const todayIso = () => { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); };   // LOCAL date — toISOString is UTC and lands a day early in SAST (same bug as the editor's +14d chip)

const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
                     'September', 'October', 'November', 'December'];
const MONTHS_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Tokens OFFERED as clickable pills. The backend batch_service.replace_tokens applies all of these
// to BOTH the batch notes AND each line description (owner 2026-09-19): [MONTH]/[YEAR] (v2 parity)
// plus [DATE] (dd/mm/yyyy), [BATCH NAME] and [COMPANY NAME]. [MONTHA] stays resolved but hidden.
export const NOTES_TOKENS = ['[MONTH]', '[YEAR]', '[DATE]', '[BATCH NAME]', '[COMPANY NAME]'];

// Friendly-token resolver (Andre: "support tokens … in a more user friendly way") — client-side
// fallback for the live preview, and for the run screen when the preview endpoint 404s. [MONTHA]
// (short month, e.g. Sep) is NOT a v2 token and is no longer offered in the picker, but is still
// resolved here so any note that already contains it keeps working; it is replaced BEFORE [MONTH]
// since it contains that token as a prefix.
export function resolveTokens(text, isoDate, ctx){
  const s = String(isoDate || '').slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  const now = new Date();
  const year = m ? Number(m[1]) : now.getFullYear();
  const monthIdx = m ? Number(m[2]) - 1 : now.getMonth();
  const dayStr = m ? m[3] : String(now.getDate()).padStart(2, '0');
  const monStr = m ? m[2] : String(now.getMonth() + 1).padStart(2, '0');
  const dateStr = dayStr + '/' + monStr + '/' + String(year);   // [DATE] = dd/mm/yyyy (backend parity)
  const c = ctx || {};
  return String(text == null ? '' : text)
    .split('[MONTHA]').join(MONTHS_ABBR[monthIdx] || '')
    .split('[MONTH]').join(MONTHS_FULL[monthIdx] || '')
    .split('[YEAR]').join(String(year))
    .split('[DATE]').join(dateStr)
    .split('[BATCH NAME]').join(c.batchName == null ? '' : String(c.batchName))
    .split('[COMPANY NAME]').join(c.companyName == null ? '' : String(c.companyName));
}

// Human-readable label for a friendly token, shown in the notes "Insert a token" dropdown so the
// options explain themselves (owner 2026-09-06: the bare [MONTH]/[MONTHA]/[YEAR] chips gave no
// hint what each did — "I do not know what MONTHA is"). The live example is resolved against
// today's date, so [MONTHA] literally reads "e.g. Sep" and [MONTH] "e.g. September".
export function tokenOptionLabel(tok){
  const desc = tok === '[MONTH]'        ? t('batch.tok_month',   'full month name')
             : tok === '[MONTHA]'       ? t('batch.tok_montha',  'short month name')
             : tok === '[YEAR]'         ? t('batch.tok_year',    'year')
             : tok === '[DATE]'         ? t('batch.tok_date',    'invoice date, dd/mm/yyyy')
             : tok === '[BATCH NAME]'   ? t('batch.tok_batch',   'this template’s name')
             : tok === '[COMPANY NAME]' ? t('batch.tok_company', 'your company name')
             : '';
  if(!desc) return tok;
  const ex = resolveTokens(tok, todayIso());   // date-based tokens resolve to a today example
  return ex && ex !== tok ? tok + ' — ' + desc + ', e.g. ' + ex : tok + ' — ' + desc;
}

// Is a template line due to be billed on a run dated `invoiceDateIso`? Mirrors the API's
// _line_is_due (batch_service.py) so the run checklist shows EXACTLY what the engine will bill:
//  • a once-off is due until the run engine CONSUMES it — the API stamps `consumed_run_id` and never
//    sends a `billed_run_id`, so reading that old key showed already-spent once-offs as due forever;
//  • a capped recurring line retires once it has fired `runs_total` times (`fired_count` is
//    audit-derived on the template GET — same guard the API applies);
//  • otherwise a recurring line is due unless the date falls outside its optional effective period.
export function isLineDueThisRun(line, invoiceDateIso){
  const ln = line || {};
  if(ln.kind === 'once_off') return ln.consumed_run_id == null;
  if(ln.runs_total && (Number(ln.fired_count) || 0) >= Number(ln.runs_total)) return false;
  const d = String(invoiceDateIso || '').slice(0, 10);
  if(ln.effective_from && d < String(ln.effective_from).slice(0, 10)) return false;
  if(ln.effective_to && d > String(ln.effective_to).slice(0, 10)) return false;
  return true;
}

function esc(s){
  return String(s == null ? '' : s).replace(/[&<>"']/g, c => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
const clearInvalidKey = (sig, k) => { if(sig.value[k]) sig.value = { ...sig.value, [k]: false }; };
// "n thing(s)" the house way — the inline `n === 1 ? one : many` ternary used all over the app
// (bank_rules.js, recurring.js, the estimate node below…), DRY'd because this screen shows the
// same count shape in ~7 places and was hard-coding the plural → "1 members" / "1 invoices" /
// "1 lines". Pass ALREADY-translated singular/plural nouns so callers keep their own t() keys.
const plural = (n, one, many) => n + ' ' + (Number(n) === 1 ? one : many);

// ── generic imperative search-drop (member + item pickers) — ported verbatim from
// document_editor.js's openDrop/closeDrop (private there); see file header for why this is
// imperative rather than signal-driven. The panel is the input's next sibling `.qi-drop`. ───────
const DROP_ITEM_SEL = '.qi-drop-item:not(.cb-drop-muted)';
function openDrop(inputEl, items, renderRow, onPick, opts){
  const drop = inputEl.nextElementSibling;
  if(!drop) return;
  const q = String(inputEl.value || '').trim().toLowerCase();
  const matches = (items || []).filter(it => opts.match(it, q)).slice(0, opts.limit || 20);
  drop.innerHTML = '';
  if(!matches.length){
    const none = document.createElement('div');
    none.className = 'qi-drop-item cb-drop-muted';
    none.textContent = t('doc.no_matches', 'No matches');
    drop.appendChild(none);
  } else {
    matches.forEach(m => {
      const row = document.createElement('div');
      row.className = 'qi-drop-item';
      row.setAttribute('role', 'option');
      row.innerHTML = renderRow(m);
      row.addEventListener('mousedown', ev => ev.preventDefault());
      row.addEventListener('click', () => { onPick(m); closeDrop(inputEl); });
      drop.appendChild(row);
    });
  }
  drop.hidden = false;
  highlightFirst(drop, { itemSelector: DROP_ITEM_SEL });
  if(!inputEl._dropAway){
    inputEl._dropAway = ev => { if(!inputEl.parentElement || !inputEl.parentElement.contains(ev.target)) closeDrop(inputEl); };
    document.addEventListener('click', inputEl._dropAway);
  }
}
function closeDrop(inputEl, revertTo){
  const drop = inputEl.nextElementSibling;
  if(drop){ drop.hidden = true; drop.innerHTML = ''; }
  if(inputEl._dropAway){ document.removeEventListener('click', inputEl._dropAway); inputEl._dropAway = null; }
  if(revertTo !== undefined) inputEl.value = revertTo;
}

function blankBatchLine(){
  return { id: null, description: '', qty: 1, unit_price: 0, tax_code_id: null, account_id: null,
           kind: 'recurring', effective_from: '', effective_to: '', consumed_run_id: null,
           runs_total: null, fired_count: 0, item_id: null, item_label: '' };
}

async function fetchTemplate(companyId, tplId){
  const r = await api.get(ep.get(companyId, tplId));
  const tplData = r.template || r.document || r;
  const memberList = Array.isArray(r.members) ? r.members : [];
  return { tplData, memberList };
}

// ── top-level dispatcher (the ONE export routed for /co/{id}/batches[/{tid}[/run]]) ────────────
export function batchesScreen(companyId, sub, id){
  if(id) return sub === 'run' ? renderRun(companyId, id) : renderEditor(companyId, id);
  return renderList(companyId);
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// LIST — house cards (name, members, last run, active pill), inline new-template form, row → editor.
// ════════════════════════════════════════════════════════════════════════════════════════════
function renderList(companyId){
  const rows = signal([]), loading = signal(true), err = signal('');
  const total = signal(0), page = signal(1), pages = signal(1);
  const PER_PAGE = 20;
  let searchTimer = null, loadSeq = 0;
  const ref = signal(null);
  const q = signal('');
  const newOpen = signal(false), newName = signal(''), newNotes = signal('');
  const newErr = signal(''), newBusy = signal(false), newInvalid = signal({});
  const delId = signal(null), delBusy = signal(false), delErr = signal('');

  async function load(){
    const requestId = ++loadSeq, requestedPage = page.value;
    loading.value = true;
    try {
      const p = new URLSearchParams({ page: String(requestedPage), per_page: String(PER_PAGE) });
      if(q.value.trim()) p.set('q', q.value.trim());
      const r = await api.get(ep.list(companyId) + '?' + p);
      if(requestId !== loadSeq) return;
      rows.value = Array.isArray(r) ? r : (r.data || []);
      total.value = Number(r.total ?? rows.value.length); pages.value = Number(r.pages || 1);
      page.value = Number(r.page || requestedPage);
      err.value = '';
    } catch(e){ err.value = (e.data && e.data.error) || t('batch.err_load', 'Could not load batch templates'); rows.value = []; }
    if(requestId === loadSeq) loading.value = false;
  }
  load();
  (async () => { try { ref.value = await api.get(ep.reference(companyId)); } catch {} })();
  const dfmt = () => (ref.value && ref.value.date_format) || 'yyyy-MM-dd';

  function openNew(){
    newName.value = ''; newNotes.value = ''; newErr.value = ''; newInvalid.value = {}; newOpen.value = true;
    queueMicrotask(() => document.querySelector('.bt-new-card input')?.focus());
  }
  function closeNew(){ if(!newBusy.value) newOpen.value = false; }
  async function createTemplate(e){
    e.preventDefault();
    if(!newName.value.trim()){ newInvalid.value = { name: true }; newErr.value = t('batch.name_required', 'Enter a name for the template.'); return; }
    newInvalid.value = {}; newBusy.value = true; newErr.value = '';
    try {
      const res = await api.post(ep.create(companyId), { name: newName.value.trim(), notes: newNotes.value.trim() || null });
      const newId = res && (res.id || (res.template && res.template.id));
      newOpen.value = false;
      if(newId) navigate('/co/' + companyId + '/batches/' + newId); else await load();
    } catch(e2){ newErr.value = (e2.data && e2.data.error) || t('batch.create_failed', 'Could not create the template'); }
    newBusy.value = false;
  }

  function openTemplate(tpl){ navigate('/co/' + companyId + '/batches/' + tpl.id); }
  function armDelete(id){ delErr.value = ''; delId.value = id; }
  function cancelDelete(){ delId.value = null; delErr.value = ''; }
  async function doDelete(id){
    delBusy.value = true; delErr.value = '';
    try { await api.post(ep.remove(companyId, id), {}); delId.value = null; await load(); }
    catch(e){ delErr.value = (e.data && e.data.error) || t('batch.delete_failed', 'Could not delete this template'); }
    delBusy.value = false;
  }

  const list = () => {
    if(loading.value) return html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`;
    if(err.value) return html`<div class="muted-block">${err.value}</div>`;
    const needle = q.value.trim().toLowerCase();
    const visible = rows.value;
    if(!visible.length) return html`<div class="bt-empty"><div class="muted-block">
        ${needle ? t('batch.no_search_matches', 'No batch templates match that search.') :
          t('batch.none_yet', 'No batch templates yet — create one to start recurring or scheduled invoicing.')}
      </div></div>`;
    const delArm = delId.value, delBusy2 = delBusy.value, delErrMsg = delErr.value, df = dfmt();
    return html`<div class="row-list">${visible.map(tpl => {
      const memberCount = tpl.member_count != null ? tpl.member_count : (Array.isArray(tpl.members) ? tpl.members.length : 0);
      const lastRun = tpl.last_run_at || tpl.last_run || null;
      const active = tpl.is_active !== false;
      return html`<div class="row-card bt-card" @click=${() => openTemplate(tpl)}>
        <div class="row-main">
          <div class="bt-name">${tpl.name}</div>
          <div class="bt-sub muted">${plural(memberCount, t('batch.member_lc', 'member'), t('batch.members_lc', 'members'))} · ${t('batch.last_run', 'Last run')}: ${lastRun ? formatDate(lastRun, df) : t('batch.never', 'Never')}</div>
        </div>
        <span class=${'st-pill ' + (active ? 'st-issued' : 'st-void')}>${active ? t('batch.active', 'Active') : t('batch.paused_tpl', 'Paused')}</span>
        <span class="bt-delete" @click=${e => e.stopPropagation()}>${rowDelete({ armed: delArm === tpl.id, busy: delBusy2 && delArm === tpl.id,
            error: delArm === tpl.id ? delErrMsg : '', onArm: () => armDelete(tpl.id), onCancel: cancelDelete, onDelete: () => doDelete(tpl.id) })}</span>
      </div>`;
    })}</div>`;
  };

  // The inline form follows the same screen flow as Service Items: title → primary action →
  // compact editor card → list. Reactive slots keep typing in place without rebuilding the input.
  const newForm = () => {
    if(!newOpen.value) return '';
    return html`<form class="card bt-new-card" @submit=${createTemplate}>
      <div class="card-body">
        <div class="bt-new-head"><h3>${t('batch.add_template', 'Add Batch Template')}</h3></div>
        <div class="bt-new-grid">
          <label class="ed-f"><span>${t('batch.name', 'Name')} *</span>
            <input class=${() => 'form-control' + (newInvalid.value.name ? ' is-invalid' : '')} .value=${newName}
              placeholder=${t('batch.name_ph', 'e.g. Monthly Retainer Clients')}
              @input=${e => { newName.value = e.target.value; clearInvalidKey(newInvalid, 'name'); if(newErr.value) newErr.value = ''; }}></label>
          <label class="ed-notes"><span>${t('doc.notes', 'Notes')}</span>
            <textarea class="form-control" rows="2" .value=${newNotes}
              placeholder=${t('batch.notes_ph', 'e.g. Thank you for your business in [MONTH]')}
              @input=${e => { newNotes.value = e.target.value; }}></textarea></label>
        </div>
        ${() => newErr.value ? html`<div class="row-err mb-2">${newErr.value}</div>` : ''}
        <div class="form-actions">
          <button type="button" class="btn btn-outline" ?disabled=${() => newBusy.value} @click=${closeNew}>${t('common.cancel', 'Cancel')}</button>
          <button type="submit" class="btn btn-primary" ?disabled=${() => newBusy.value}>${() => newBusy.value ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </div>
      </div>
    </form>`;
  };

  const content = html`<div class="page page-wide doc-page bt-list-page ux-list-page">
    <h1 class="sr-only">${t('nav.batches', 'Batch Invoicing')}</h1>
    <div class="page-head with-action bt-list-tools ux-list-actions">
      <input class="search ux-search bt-search" type="search" .value=${q}
        @input=${e => { q.value = e.target.value; page.value = 1; delId.value = null; clearTimeout(searchTimer); searchTimer = setTimeout(load, 250); }}
        placeholder=${t('batch.search_items_placeholder', 'Search templates…')}
        aria-label=${t('batch.search_items', 'Search batch templates')}>
      <div class="row-actions">
        <button class="btn btn-primary" @click=${openNew}>${icon('plus', {size:14})} ${t('batch.add_template', 'Add Batch Template')}</button>
      </div>
    </div>
    <div class="muted bt-count list-count">${() => {
      return plural(total.value, t('batch.template_lc', 'template'), t('batch.templates_lc', 'templates'));
    }}</div>
    ${newForm}
    ${list}
    ${() => pager({ page: page.value, totalPages: pages.value, total: total.value, perPage: PER_PAGE,
      onPage: next => { page.value = next; delId.value = null; load(); } })}
  </div>`;
  return tenantShell(companyId, t('nav.batches', 'Batch Invoicing'), content);
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// EDITOR — details (name/notes w/ token chips+preview/active), members (search+tag, pause/
// remove), selected member's line grid (editor-style focus-safe inputs, kind + period).
// ════════════════════════════════════════════════════════════════════════════════════════════
function renderEditor(companyId, tplId){
  const loading = signal(true), err = signal('');
  const ref = signal(null), tpl = signal(null), members = signal([]);
  const coName = companyName(companyId);   // signal → company name for the [COMPANY NAME] token preview
  const companyTags = signal([]);   // company tags for "Add by tag" (from /companies/{id}/tags)
  const fName = signal(''), fNotes = signal(''), fActive = signal(true);
  const fNextRun = signal(''), fAutoRun = signal(false);   // schedule: next_run_date (ISO) + auto_run
  const invalid = signal({}), formErr = signal(''), saving = signal(false), savedAt = signal(null);
  const selectedMemberId = signal(null);
  const memberErr = signal(''), memberBusy = signal(false), tagPick = signal('');
  const delMemberId = signal(null), delMemberBusy = signal(false), delMemberErr = signal('');
  const checkedMembers = signal(new Set());   // multi-select for bulk pause/resume/remove (QBO/Zoho pattern)
  const bulkRemoveArmed = signal(false);
  let noticeTimer; const notice = signal('');
  function setNotice(m){ notice.value = m; clearTimeout(noticeTimer); noticeTimer = setTimeout(() => { notice.value = ''; }, 3500); }

  let detailsInitialized = false;
  async function loadTpl(){
    try {
      const { tplData, memberList } = await fetchTemplate(companyId, tplId);
      tpl.value = tplData; members.value = memberList;
      if(!detailsInitialized){
        fName.value = tplData.name || ''; fNotes.value = tplData.notes || '';
        fActive.value = tplData.is_active !== false;
        fNextRun.value = tplData.next_run_date ? String(tplData.next_run_date).slice(0, 10) : '';
        fAutoRun.value = tplData.auto_run === true;
        detailsInitialized = true;
      }
      err.value = '';
    } catch(e){ err.value = (e.data && e.data.error) || t('batch.err_load_one', 'Could not load this template'); }
  }
  async function loadRef(){
    try { ref.value = await api.get(ep.reference(companyId)); if(ref.value) configureSleekDate(ref.value); }
    catch { /* non-fatal — screens without it still render */ }
  }
  async function loadTags(){
    try { const r = await api.get(ep.tags(companyId)); companyTags.value = (r && r.data) || []; }
    catch { /* non-fatal — the "Add by tag" picker just stays hidden */ }
  }
  (async () => { await loadRef(); loadTags(); await loadTpl(); loading.value = false; })();

  const sym = () => (ref.value && ref.value.currency_symbol) || baseCurrencySymbol();

  // ── details save ──────────────────────────────────────────────────────────────────────────
  async function saveDetails(){
    if(!fName.value.trim()){ invalid.value = { name: true }; formErr.value = t('batch.name_required', 'Enter a name for the template.'); return; }
    invalid.value = {}; formErr.value = ''; saving.value = true;
    try {
      await api.put(ep.update(companyId, tplId), { name: fName.value.trim(), notes: fNotes.value.trim() || null, is_active: fActive.value,
        next_run_date: fNextRun.value || null, auto_run: !!fAutoRun.value });
      savedAt.value = new Date();
    } catch(e){ formErr.value = (e.data && e.data.error) || t('batch.save_failed', 'Could not save the template'); }
    saving.value = false;
  }
  // Insert a token at the caret of the CURRENTLY-FOCUSED editable field — the notes textarea
  // (.bt-notes-input) OR a line-description input (.bt-desc-in). The pill buttons use
  // @mousedown=preventDefault so focus never leaves that field, so document.activeElement is still
  // the right target when the click fires. Notes are reactive-bound to fNotes (write the signal);
  // a line description is a plain-value input mutated in place (set .value + dispatch 'input' so the
  // line's own @input handler updates ln.description and debounce-saves — no signal write, no
  // re-render, focus/caret survive). Falls back to the notes textarea when nothing editable holds
  // focus (e.g. clicking a notes pill on first load).
  function insertToken(tok){
    const el = document.activeElement;
    const focused = el && el.classList && (el.classList.contains('bt-notes-input') || el.classList.contains('bt-desc-in')) ? el : null;
    const target = focused || document.querySelector('.bt-notes-input');
    if(!target){ fNotes.value = (fNotes.value || '') + tok; return; }
    const start = target.selectionStart != null ? target.selectionStart : target.value.length;
    const end = target.selectionEnd != null ? target.selectionEnd : target.value.length;
    const newVal = target.value.slice(0, start) + tok + target.value.slice(end);
    const pos = start + tok.length;
    if(target.classList.contains('bt-notes-input')){
      fNotes.value = newVal;                                   // reactive-bound textarea
    } else {
      target.value = newVal;
      target.dispatchEvent(new Event('input', { bubbles: true }));   // → lineInput mutates ln + saves
    }
    requestAnimationFrame(() => { target.focus(); try { target.setSelectionRange(pos, pos); } catch {} });
  }

  // ── members: search-add, tag-add (feature-detected), pause/resume, remove ───────────────────
  function tagOptions(){
    const list = companyTags.value;   // [{id, code, label, color, kind}] from /companies/{id}/tags
    return Array.isArray(list) && list.length ? list : null;
  }
  async function pickMember(c){
    memberBusy.value = true; memberErr.value = '';
    try { await api.post(ep.addMember(companyId, tplId), { contact_id: c.id }); await loadTpl(); }
    catch(e){ memberErr.value = (e.data && e.data.error) || t('batch.member_add_failed', 'Could not add this member'); }
    memberBusy.value = false;
  }
  function onMemberSearch(e){
    const already = new Set(members.value.map(m => m.contact_id));
    const cs = ((ref.value && ref.value.contacts) || []).filter(c => c.is_customer && !already.has(c.id));
    openDrop(e.target, cs,
      c => '<span>' + esc(c.code ? c.code + ' · ' : '') + esc(c.name || '') + '</span>',
      c => pickMember(c),
      { match: (c, q) => !q || String(c.name || '').toLowerCase().includes(q) || String(c.code || '').toLowerCase().includes(q) });
  }
  function onMemberKeydown(e){
    if(e.key === 'Escape'){ closeDrop(e.target, ''); return; }
    dropdownKeydown(e, e.target.nextElementSibling, { wrap: false, itemSelector: DROP_ITEM_SEL });
  }
  function onMemberBlur(e){
    const input = e.target;
    setTimeout(() => { if(document.activeElement !== input) closeDrop(input, ''); }, 120);
  }
  async function addByTag(){
    const tagId = tagPick.value; if(!tagId) return;
    memberBusy.value = true; memberErr.value = '';
    try { await api.post(ep.addMember(companyId, tplId), { tag_id: Number(tagId) }); tagPick.value = ''; await loadTpl(); }
    catch(e){ memberErr.value = (e.data && e.data.error) || t('batch.member_add_failed', 'Could not add this member'); }
    memberBusy.value = false;
  }
  function selectMember(id){
    // Golden rule #7 (edit in place): the lines grid now renders INLINE inside the selected
    // member's own card (see membersCard → renderLinesGrid), so there is no separate bottom panel
    // to scroll to — toggling the selection expands/collapses the card where it sits.
    selectedMemberId.value = selectedMemberId.value === id ? null : id;
  }
  async function togglePause(m){
    try { await api.post(ep.pauseMember(companyId, tplId, m.id), { paused: !m.paused }); await loadTpl(); }
    catch(e){ setNotice((e.data && e.data.error) || t('batch.pause_failed', 'Could not update this member')); }
  }
  function armMemberDelete(id){ delMemberErr.value = ''; delMemberId.value = id; }
  function cancelMemberDelete(){ delMemberId.value = null; delMemberErr.value = ''; }
  async function doMemberDelete(id){
    delMemberBusy.value = true; delMemberErr.value = '';
    try {
      await api.post(ep.removeMember(companyId, tplId, id), {});
      delMemberId.value = null;
      if(selectedMemberId.value === id) selectedMemberId.value = null;
      await loadTpl();
    } catch(e){ delMemberErr.value = (e.data && e.data.error) || t('batch.member_remove_failed', 'Could not remove this member'); }
    delMemberBusy.value = false;
  }

  // ── bulk member ops (multi-select): pause / resume / remove many at once (QBO/Zoho) ───────────
  function toggleChecked(id){
    const s = new Set(checkedMembers.value);
    if(s.has(id)) s.delete(id); else s.add(id);
    checkedMembers.value = s;
    if(!s.size) bulkRemoveArmed.value = false;
  }
  function clearChecked(){ checkedMembers.value = new Set(); bulkRemoveArmed.value = false; }
  async function bulkPause(paused){
    const ids = [...checkedMembers.value]; if(!ids.length) return;
    memberBusy.value = true; memberErr.value = '';
    try { for(const id of ids){ await api.post(ep.pauseMember(companyId, tplId, id), { paused }); }
          clearChecked(); await loadTpl(); }
    catch(e){ setNotice((e.data && e.data.error) || t('batch.pause_failed', 'Could not update these members')); }
    memberBusy.value = false;
  }
  async function bulkRemove(){
    const ids = [...checkedMembers.value]; if(!ids.length) return;
    memberBusy.value = true; memberErr.value = '';
    try {
      for(const id of ids){
        await api.post(ep.removeMember(companyId, tplId, id), {});
        if(selectedMemberId.value === id) selectedMemberId.value = null;
      }
      clearChecked(); await loadTpl();
    } catch(e){ setNotice((e.data && e.data.error) || t('batch.member_remove_failed', 'Could not remove these members')); }
    memberBusy.value = false;
  }

  // ── lines: mutate-in-place for keystrokes, reassign members.value for structural changes ────
  function structuralUpdate(){ members.value = [...members.value]; }
  function scheduleLineSave(mid, ln){
    clearTimeout(ln._timer);
    // Chain saves for THIS line so a create can never race a second save into a duplicate POST:
    // the next persist waits for the in-flight one to land (and set ln.id → the following save PUTs).
    ln._timer = setTimeout(() => {
      ln._chain = Promise.resolve(ln._chain).catch(() => {}).then(() => persistLine(mid, ln));
    }, 600);
  }
  async function persistLine(mid, ln){
    const body = {
      member_id: mid, description: ln.description, qty: Number(ln.qty) || 0, unit_price: Number(ln.unit_price) || 0,
      tax_code_id: ln.tax_code_id || null, account_id: ln.account_id || null, kind: ln.kind || 'recurring',
      effective_from: ln.kind === 'recurring' ? (ln.effective_from || null) : null,
      effective_to: ln.kind === 'recurring' ? (ln.effective_to || null) : null,
      runs_total: ln.kind === 'recurring' ? (Number(ln.runs_total) || null) : null,
    };
    try {
      if(ln.id){ await api.put(ep.updateLine(companyId, ln.id), body); }
      else {
        const res = await api.post(ep.addLine(companyId, mid), body);
        // add_line returns {success, line_id} — NOT {id}. Reading the wrong key left ln.id null,
        // so every debounced save re-POSTed a NEW line (silent duplicate → over-billing on run)
        // and removeLine could not delete server-side. Keep `id` as a forward-compat fallback.
        const newId = res && (res.line_id || res.id);
        if(newId) ln.id = newId;
      }
    } catch(e){ setNotice((e.data && e.data.error) || t('batch.line_save_failed', 'Could not save this line')); }
  }
  function lineInput(mid, ln, key, val){ ln[key] = val; scheduleLineSave(mid, ln); }
  // Per-line Tax / Account are iT searchSelects (S14 dropdown parity; editor.js's tr._vatSel /
  // tr._acctSel pattern). The grid re-renders wholesale on structuralUpdate, so each LINE object
  // caches its instances and the same node is MOVED into every fresh render; options + shown value
  // re-sync here because the reference bundle loads async and prefillLineFromItem writes the ids
  // behind the control's back. The empty option keeps wave's nullable tax/account clearable.
  function lineTaxSel(mid, ln, taxes, retired){
    if(!ln._taxSel){
      ln._taxSel = searchSelect({
        ariaLabel: t('doc.tax', 'Tax'),
        onSelect: v => lineInput(mid, ln, 'tax_code_id', v ? Number(v) : null),
      });
    }
    ln._taxSel.setOptions([{ value: '', label: t('doc.no_tax', 'No tax') },
      // code + description + rate so the user knows what each VAT code is for (owner 2026-09-08)
      ...taxes.map(tx => ({ value: String(tx.id),
        label: [tx.code, tx.name, (tx.rate != null ? tx.rate + '%' : null)].filter(Boolean).join(' · '),
        keywords: tx.name || '' }))]);
    ln._taxSel.set(ln.tax_code_id == null ? '' : String(ln.tax_code_id));
    ln._taxSel.input.disabled = !!retired;
    return ln._taxSel.el;
  }
  function lineAcctSel(mid, ln, accts, retired){
    if(!ln._acctSel){
      ln._acctSel = searchSelect({
        ariaLabel: t('doc.account', 'Account'),
        onSelect: v => lineInput(mid, ln, 'account_id', v ? Number(v) : null),
      });
    }
    // Label carries BOTH code and name ("1050 · Advertising") — a bare `sub` put the name in a
    // right-aligned muted column that split off from the code and, once picked, left the field
    // showing only the number ("1050"), which read as "the account isn't shown". (owner 2026-09-09)
    ln._acctSel.setOptions([{ value: '', label: t('doc.acct_ph', '— account —') },
      ...accts.map(a => ({ value: String(a.id),
        label: (a.code ? a.code + ' · ' : '') + (a.name || ''),
        keywords: (a.name || '') + ' ' + (a.code || '') }))]);
    ln._acctSel.set(ln.account_id == null ? '' : String(ln.account_id));
    ln._acctSel.input.disabled = !!retired;
    return ln._acctSel.el;
  }
  // Item — the SAME house searchSelect combobox as Tax/Account and the documents editor: type to
  // filter the catalogue, arrow / Enter / Tab to pick; picking prefills the row (description, price,
  // account, tax). Its popup is body-appended so it is NEVER clipped by the grid's horizontal scroll
  // — the old hand-rolled .qi-drop was clipped, which is why item search felt broken inside the grid.
  function lineItemSel(mid, ln, retired){
    if(!ln._itemSel){
      ln._itemSel = searchSelect({
        ariaLabel: t('doc.item', 'Item'),
        placeholder: t('doc.item_search_ph', 'Search items… e.g. Consulting'),
        onSelect: v => { const it = ((ref.value && ref.value.items) || []).find(x => String(x.id) === String(v)); if(it) prefillLineFromItem(mid, ln, it); },
      });
    }
    ln._itemSel.setOptions(((ref.value && ref.value.items) || []).map(it => ({
      value: String(it.id), label: (it.code ? it.code + ' · ' : '') + (it.name || ''),
      sub: it.price != null ? fmtMoney(it.price, sym(), false) : '', keywords: (it.name || '') + ' ' + (it.code || '') })));
    ln._itemSel.set(ln.item_id != null ? String(ln.item_id) : '');
    ln._itemSel.input.disabled = !!retired;
    return ln._itemSel.el;
  }
  function changeLineKind(mid, ln, val){
    ln.kind = val;
    if(val === 'once_off'){ ln.effective_from = ''; ln.effective_to = ''; }
    structuralUpdate(); scheduleLineSave(mid, ln);
  }
  function changeLinePeriod(mid, ln, key, val){ ln[key] = val; structuralUpdate(); scheduleLineSave(mid, ln); }
  // End-mode select (owner 2026-09-05: "'indefinite' should be a selection … at the moment it
  // cannot be clicked"): ∞ Indefinite = clear the end date and run forever; Until… = reveal the
  // end-date picker (saved once a date is chosen). `_endMode` is client-only UI state.
  function changeEndMode(mid, ln, mode){
    ln._endMode = mode;
    // The three end modes are mutually exclusive: picking one clears the other's bound (S17 adds
    // 'after' — the QuickBooks/Zoho "ends after N occurrences" — as runs_total, migration 101).
    let dirtied = false;
    if(mode !== 'until' && ln.effective_to){ ln.effective_to = null; dirtied = true; }
    if(mode !== 'after' && ln.runs_total){ ln.runs_total = null; dirtied = true; }
    structuralUpdate();
    if(dirtied) scheduleLineSave(mid, ln);
  }
  function addLineFor(mid){
    const m = members.value.find(x => x.id === mid); if(!m) return;
    m.lines = [...(m.lines || []), blankBatchLine()];
    structuralUpdate();
  }
  async function removeLine(mid, ln){
    const m = members.value.find(x => x.id === mid); if(!m) return;
    m.lines = (m.lines || []).filter(l => l !== ln);
    structuralUpdate();
    if(ln.id){
      try { await api.post(ep.removeLine(companyId, ln.id), {}); }
      catch(e){ setNotice((e.data && e.data.error) || t('batch.line_remove_failed', 'Could not remove this line')); }
    }
  }
  // Picking a catalogue item prefills the line (label + description + price + account + tax). We
  // re-sync THIS row's controls IN PLACE rather than a wholesale structuralUpdate() — a full
  // re-render would detach the row mid-keystroke and break the keyboard Enter-walk (focus can't move
  // to a cell that just got replaced). Description/Price are plain inputs (set .value directly);
  // Tax/Account are cached searchSelects (.set()); nothing about kind/period/retired changes here.
  function prefillLineFromItem(mid, ln, it){
    ln.item_id = it.id;   // client-only convenience — NOT sent (add-line contract carries no item_id)
    ln.item_label = (it.code ? it.code + ' · ' : '') + (it.name || '');
    if(!ln.description) ln.description = it.name || '';
    if(it.price != null) ln.unit_price = it.price;
    if(it.sales_account_id != null) ln.account_id = it.sales_account_id;
    if(it.sales_tax_code_id != null) ln.tax_code_id = it.sales_tax_code_id;
    const tr = ln._itemSel && ln._itemSel.input.closest('tr');
    if(tr){
      const descIn = tr.querySelector('.bt-desc-in'), priceIn = tr.querySelector('.bt-price-in');
      if(descIn) descIn.value = ln.description || '';
      if(priceIn) priceIn.value = ln.unit_price != null ? ln.unit_price : '';
    }
    if(ln._taxSel) ln._taxSel.set(ln.tax_code_id == null ? '' : String(ln.tax_code_id));
    if(ln._acctSel) ln._acctSel.set(ln.account_id == null ? '' : String(ln.account_id));
    scheduleLineSave(mid, ln);
  }
  // Enter walks the line grid like the documents editor (Xero pattern): commit the current cell,
  // then move focus to the next input in the row — Item → Description → Qty → Price → Tax → Account —
  // and off the last cell into the next row (or a fresh line). searchSelect commits its highlighted
  // option on Enter FIRST (target-phase keydown), so by the time this bubble-phase handler runs the
  // pick is done and we just advance. An empty combobox Enter is left alone (it bubbles harmlessly).
  function onLineEnterWalk(e, mid){
    if(e.key !== 'Enter') return;
    const target = e.target;
    const isPlain = target.matches && target.matches('td > input');
    const isSS = target.classList && target.classList.contains('ss-input');
    if(!isPlain && !isSS) return;
    if(isSS && !String(target.value || '').trim()) return;   // empty combobox Enter → don't jump
    e.preventDefault();
    const tr = e.currentTarget;
    const inputs = [...tr.querySelectorAll('td > input, .ss-input')].filter(el => !el.disabled);
    const next = inputs[inputs.indexOf(target) + 1];
    if(next){ next.focus(); try { next.select && next.select(); } catch {} return; }
    const nextRow = tr.nextElementSibling;
    if(nextRow && nextRow.matches('tr')){ (nextRow.querySelector('.ss-input, td > input'))?.focus(); return; }
    addLineFor(mid);
  }

  // ── render: each const below is an independent TOP-LEVEL slot (see file header) ─────────────
  const detailsFields = () => {
    // Built once: no signal is read at the top, so writes to invalid / formErr / saving / savedAt
    // don't re-run this and rebuild the name input (the tina4 focus-trap). They drive in-place
    // reactive bindings below instead — so clearing the error as the user types keeps the caret.
    return html`<div>
      <label class="ed-f"><span>${t('batch.name', 'Name')} *</span>
        <input class=${() => 'form-control' + (invalid.value.name ? ' is-invalid' : '')} .value=${fName}
          placeholder=${t('batch.name_ph', 'e.g. Monthly Retainer Clients')}
          @input=${e => { fName.value = e.target.value; clearInvalidKey(invalid, 'name'); if(formErr.value) formErr.value = ''; }}></label>
      <label class="switch mt-2"><input type="checkbox" .checked=${fActive}
          @change=${e => { fActive.value = e.target.checked; }}><span class="switch-track"></span> ${t('batch.active_label', 'Active')}</label>
      <div class="bt-schedule mt-2">
        <label class="ed-f bt-sched-date"><span>${t('batch.next_run_date', 'Next run date')}</span>
          <sleek-date data-free="true" .value=${fNextRun}
            aria-label=${t('batch.next_run_date', 'Next run date')}
            @change=${e => { fNextRun.value = e.target.value; }}></sleek-date>
          <span class="row-sub bt-sched-hint">${t('batch.next_run_hint', 'Blank = not scheduled')}</span></label>
        <div class="bt-sched-auto">
          <label class="switch"><input type="checkbox" .checked=${fAutoRun}
              @change=${e => { fAutoRun.value = e.target.checked; }}><span class="switch-track"></span> ${t('batch.auto_run', 'Auto run')}</label>
          <span class="row-sub bt-sched-hint">${t('batch.auto_run_hint', 'On: runs automatically on the next run date. Off: appears on the dashboard to release manually.')}</span>
        </div>
      </div>
      <label class="ed-notes mt-2"><span>${t('doc.notes', 'Notes')}</span>
        <div class="bt-token-pills" role="group" aria-label=${t('batch.insert_token', 'Insert a token')}>
          <span class="bt-token-pills-lead">${t('batch.insert_token_lead', 'Insert:')}</span>
          ${NOTES_TOKENS.map(tok => html`<button type="button" class="bt-token-pill" title=${tokenOptionLabel(tok)}
            @mousedown=${e => e.preventDefault()} @click=${() => insertToken(tok)}>${tok}</button>`)}
        </div>
        <textarea class="form-control bt-notes-input" rows="3" .value=${fNotes}
          placeholder=${t('batch.notes_ph', 'e.g. Thank you for your business in [MONTH]')}
          @input=${e => { fNotes.value = e.target.value; }}></textarea></label>
      ${() => formErr.value ? html`<div class="row-err edit-err">${formErr.value}</div>` : ''}
      <div class="edit-actions">
        <button type="button" class="btn btn-primary" ?disabled=${() => saving.value} @click=${saveDetails}>${() => saving.value ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        ${() => savedAt.value ? html`<span class="row-sub">${t('doc.saved_at', 'Saved')}</span>` : ''}
      </div>
    </div>`;
  };
  // Isolated so ONLY notes-driven keystrokes re-render this (no inputs live here to lose focus).
  const notesPreview = () => {
    if(!String(fNotes.value || '').trim()) return '';
    // Resolve every token to an example value: date tokens against today, [BATCH NAME] against the
    // live name field, [COMPANY NAME] against the loaded company name (a signal from the shell).
    const resolved = resolveTokens(fNotes.value, todayIso(),
      { batchName: fName.value || (tpl.value && tpl.value.name) || '', companyName: coName.value || '' });
    return html`<div class="bt-token-preview row-sub"><span class="bt-token-preview-lead">${t('batch.will_read', 'This will read')}:</span> ${resolved}</div>`;
  };

  const membersCard = () => {
    const ms = members.value, sel = selectedMemberId.value;
    const dArm = delMemberId.value, dBusy = delMemberBusy.value, dErr = delMemberErr.value;
    const mErr = memberErr.value, tags = tagOptions(), tagSel = tagPick.value;
    const chk = checkedMembers.value, bulkArmed = bulkRemoveArmed.value, mBusy = memberBusy.value;
    return html`<div class="card bt-addmember">
      <h3>${t('batch.members', 'Members')} <span class="grid-count">${ms.length}</span></h3>
      <div class="qi-f">
        <input class="form-control" placeholder=${t('batch.member_search_ph', 'Search contacts… e.g. Acme')}
          @focus=${onMemberSearch} @input=${onMemberSearch} @keydown=${onMemberKeydown} @blur=${onMemberBlur}
          role="combobox" aria-expanded="false" aria-label=${t('batch.member_search_ph', 'Search contacts')}>
        <div class="qi-drop" hidden></div>
      </div>
      ${tags ? html`<div class="bt-tagrow mt-2">
          <select class="form-control" .value=${tagPick} @change=${e => { tagPick.value = e.target.value; }}
            aria-label=${t('batch.add_by_tag', 'Add by tag')}>
            <option value="">${t('batch.select_tag', '— Select a tag —')}</option>
            ${tags.map(tg => html`<option value=${tg.id}>${tg.label}</option>`)}
          </select>
          <button type="button" class="btn btn-sm btn-outline" ?disabled=${!tagSel} @click=${addByTag}>${t('batch.add_by_tag', 'Add by tag')}</button>
        </div>` : ''}
      ${mErr ? html`<div class="row-err mt-1">${mErr}</div>` : ''}
      ${chk.size ? html`<div class="bt-bulkbar">
        <span class="bt-bulk-n">${chk.size} ${t('batch.selected', 'selected')}</span>
        ${bulkArmed
          ? html`<span class="bt-bulk-warn">${t('batch.remove_these', 'Remove these members?')}</span>
              <button type="button" class="btn btn-sm btn-danger" ?disabled=${mBusy} @click=${bulkRemove}>${t('batch.yes_remove', 'Yes, remove')}</button>
              <button type="button" class="btn btn-sm btn-outline" ?disabled=${mBusy} @click=${() => { bulkRemoveArmed.value = false; }}>${t('common.cancel', 'Cancel')}</button>`
          : html`<button type="button" class="btn btn-sm btn-outline" ?disabled=${mBusy} @click=${() => bulkPause(true)}>${t('batch.pause', 'Pause')}</button>
              <button type="button" class="btn btn-sm btn-outline" ?disabled=${mBusy} @click=${() => bulkPause(false)}>${t('batch.resume', 'Resume')}</button>
              <button type="button" class="btn btn-sm btn-outline btn-danger-text" ?disabled=${mBusy} @click=${() => { bulkRemoveArmed.value = true; }}>${t('batch.remove', 'Remove')}</button>
              <button type="button" class="btn btn-sm btn-link" @click=${clearChecked}>${t('common.clear', 'Clear')}</button>`}
      </div>` : ''}
      ${!ms.length ? html`<div class="bt-noline muted">${t('batch.no_members', 'No members yet — search above to add one.')}</div>` : html`
      <div class="bt-members" role="listbox" aria-label=${t('batch.members', 'Members')}>${ms.map(m => html`
        <div class=${'card bt-member' + (m.paused ? ' bt-paused' : '') + (sel === m.id ? ' active' : '')}
            role="option" tabindex="0" aria-selected=${sel === m.id ? 'true' : 'false'}
            @click=${() => selectMember(m.id)}
            @keydown=${e => { if(e.key === 'Enter' || e.key === ' '){ e.preventDefault(); selectMember(m.id); } }}>
          <div class="bt-member-head">
            <div class="bt-member-lead">
              <label class="bt-member-check" @click=${e => e.stopPropagation()}><input type="checkbox" .checked=${chk.has(m.id)}
                @change=${() => toggleChecked(m.id)} aria-label=${t('batch.select_for_bulk', 'Select for bulk action')}></label>
              <div class="bt-member-name">${m.contact_name}${m.paused ? html`<span class="bt-pausetag">${t('batch.paused', 'Paused')}</span>` : ''}</div>
            </div>
            <div class="bt-member-act" @click=${e => e.stopPropagation()}>
              <button type="button" class="btn btn-sm btn-outline" @click=${() => togglePause(m)}>${m.paused ? t('batch.resume', 'Resume') : t('batch.pause', 'Pause')}</button>
              ${rowDelete({ armed: dArm === m.id, busy: dBusy && dArm === m.id, error: dArm === m.id ? dErr : '',
                  onArm: () => armMemberDelete(m.id), onCancel: cancelMemberDelete, onDelete: () => doMemberDelete(m.id) })}
            </div>
          </div>
          <div class="bt-sub muted">${plural((m.lines || []).length, t('batch.line_lc', 'line'), t('batch.lines_lc', 'lines'))}${sel === m.id ? html`<span class="bt-member-editing"> · ${t('batch.editing_now', 'editing')}</span>` : ''}</div>
          ${sel === m.id ? renderLinesGrid(m) : ''}
        </div>`)}</div>`}
    </div>`;
  };

  // Renders the per-member line grid INLINE inside the selected member's card (golden rule #7 —
  // edit in place). Called from membersCard's .map() for the SELECTED member only; it is a plain
  // function call, NOT a reactive ${() => …} slot, so line keystrokes (which mutate the line object
  // in place — no signal write — see lineInput/scheduleLineSave) never rebuild it and focus/caret
  // survive, exactly the isolation the old detached bottom panel had. The .map() only re-runs on
  // members.value / selectedMemberId.value changes (structural), never on a keystroke. The wrapper
  // stops click + keydown from bubbling to the card's select handler so a click or an Enter-walk
  // inside the grid can't toggle the member card shut (Enter/Space would otherwise re-select).
  function renderLinesGrid(member){
    const mid = member.id;
    const taxes = (ref.value && ref.value.tax_codes) || [];
    const accts = (ref.value && ref.value.accounts) || [];
    const lines = member.lines || [];
    return html`<div class="bt-member-lines" @click=${e => e.stopPropagation()} @keydown=${e => e.stopPropagation()}>
      <div class="bt-token-pills bt-line-tokens" role="group" aria-label=${t('batch.insert_token', 'Insert a token')}>
        <span class="bt-token-pills-lead">${t('batch.insert_into_line', 'Insert token into the focused field:')}</span>
        ${NOTES_TOKENS.map(tok => html`<button type="button" class="bt-token-pill" title=${tokenOptionLabel(tok)}
          @mousedown=${e => e.preventDefault()} @click=${() => insertToken(tok)}>${tok}</button>`)}
      </div>
      ${!lines.length ? html`<div class="bt-noline muted">${t('batch.no_lines', 'No lines yet.')}</div>` : html`
      <div class="table-wrap edit-lines bt-lines-wrap"><table class="doc-edit-tbl bt-line-tbl">
        <thead><tr><th class="edln-item">${t('doc.item', 'Item')}</th><th class="edln-desc">${t('doc.description', 'Description')}</th>
          <th class="edln-n r">${t('doc.qty', 'Qty')}</th><th class="edln-n r">${t('doc.price_incl', 'Price (incl.)')}</th>
          <th class="edln-tax">${t('doc.tax', 'Tax')}</th><th class="edln-acct">${t('doc.account', 'Account')}</th>
          <th class="edln-kind">${t('batch.kind', 'Kind')}</th><th class="edln-x"></th></tr></thead>
        <tbody>${lines.map((ln, i) => {
          const retired = ln.kind === 'once_off' && ln.consumed_run_id != null;
          // S17: a recurring line that has billed its full runs_total wears the retired look too,
          // but stays EDITABLE — raising/clearing the cap re-arms it (unlike a consumed once-off).
          const ended = ln.kind !== 'once_off' && ln.runs_total && (ln.fired_count || 0) >= ln.runs_total;
          const retClass = (retired || ended ? ' bt-line-retired' : '');
          // Each line spans TWO rows (owner 2026-09-19): row 1 = Item·Description·Qty·Price·Tax·
          // Account·Kind·✕; row 2 = the recurrence Period, full-width and indented so it reads as
          // this line's own second row (no divider between them; a divider below the period row
          // separates one line from the next). tina4-js `html` returns all root nodes, so the
          // two sibling <tr>s from one map iteration both land in the <tbody>.
          return html`<tr class=${'edln edln-main' + retClass} @keydown=${e => onLineEnterWalk(e, mid)}>
            <td class="edln-item" data-th=${t('doc.item', 'Item')}>${lineItemSel(mid, ln, retired)}</td>
            <td class="edln-desc" data-th=${t('doc.description', 'Description')}><input class="form-control bt-desc-in" aria-label=${t('doc.description', 'Description')} .value=${ln.description} ?disabled=${retired}
              placeholder=${t('doc.desc_ph', 'e.g. Consulting — May')}
              @input=${e => lineInput(mid, ln, 'description', e.target.value)}></td>
            <td class="edln-n" data-th=${t('doc.qty', 'Qty')}><input class="form-control text-right" aria-label=${t('doc.qty', 'Qty')} type="number" step="0.01"
              .value=${ln.qty} ?disabled=${retired} placeholder="e.g. 1"
              @input=${e => lineInput(mid, ln, 'qty', e.target.value)}></td>
            <td class="edln-n" data-th=${t('doc.price_incl', 'Price (incl.)')}><input class="form-control text-right mono bt-price-in" aria-label=${t('doc.price_incl', 'Price (incl.)')} type="number" step="0.01"
              .value=${ln.unit_price} ?disabled=${retired} placeholder="e.g. 1500.00"
              @input=${e => lineInput(mid, ln, 'unit_price', e.target.value)}></td>
            <td class="edln-tax" data-th=${t('doc.tax', 'Tax')}>${lineTaxSel(mid, ln, taxes, retired)}</td>
            <td class="edln-acct" data-th=${t('doc.account', 'Account')}>${lineAcctSel(mid, ln, accts, retired)}</td>
            <td class="edln-kind" data-th=${t('batch.kind', 'Kind')}>
              <select class="form-control" aria-label=${t('batch.kind', 'Kind')} ?disabled=${retired} @change=${e => changeLineKind(mid, ln, e.target.value)}>
                <option value="recurring" ?selected=${ln.kind !== 'once_off'}>${t('batch.kind_recurring', 'Recurring')}</option>
                <option value="once_off" ?selected=${ln.kind === 'once_off'}>${t('batch.kind_once_off', 'Once-off')}</option>
              </select></td>
            <td class="edln-x"><button type="button" class="edln-del" title=${t('doc.remove_line', 'Remove line')} aria-label=${t('doc.remove_line', 'Remove line')}
              @click=${() => removeLine(mid, ln)}>✕</button></td>
          </tr>
          <tr class=${'edln edln-period-row' + retClass}>
            <td class="edln-period-cell" colspan="8" data-th=${t('batch.period', 'Period')}>
              ${ln.kind === 'once_off'
                ? (retired ? html`<span class="row-sub">${t('batch.billed_in_run', 'Billed in run')} #${ln.consumed_run_id}</span>`
                           : html`<span class="row-sub">⏰ ${t('batch.fires_next_run', 'Fires next run')}</span>`)
                : (() => {
                    // S17: three mutually-exclusive end modes — ∞ / Until a date / After N runs.
                    const em = ln._endMode || (ln.runs_total ? 'after' : ln.effective_to ? 'until' : 'indefinite');
                    return html`<div class="bt-period-row">
                    <label class="bt-period-fld"><span class="bt-period-lbl">${t('batch.period_from', 'From')}</span>
                      <sleek-date data-free="true" aria-label=${t('batch.start_date', 'Start date')} title=${t('batch.from_date_hint', 'Start billing from this date — blank starts at the next run')}
                        .value=${ln.effective_from || ''} @change=${e => changeLinePeriod(mid, ln, 'effective_from', e.target.value)}></sleek-date></label>
                    <label class="bt-period-fld"><span class="bt-period-lbl">${t('batch.period_until', 'Until / for')}</span>
                      <select class="form-control bt-endmode" aria-label=${t('batch.end_mode', 'End mode')} ?disabled=${retired}
                        title=${t('batch.endmode_hint', 'How long this line keeps billing')}
                        @change=${e => changeEndMode(mid, ln, e.target.value)}>
                        <option value="indefinite" ?selected=${em === 'indefinite'}>∞ ${t('batch.indefinite', 'Indefinite')}</option>
                        <option value="until" ?selected=${em === 'until'}>${t('batch.until_date', 'Until a date…')}</option>
                        <option value="after" ?selected=${em === 'after'}>${t('batch.after_runs', 'After N runs…')}</option>
                      </select></label>
                    ${em === 'until' ? html`<sleek-date data-free="true" aria-label=${t('batch.end_date', 'End date')}
                      title=${t('batch.to_date_hint', 'Last date this line bills')}
                      .value=${ln.effective_to || ''} @change=${e => changeLinePeriod(mid, ln, 'effective_to', e.target.value)}></sleek-date>` : ''}
                    ${em === 'after' ? html`<input class="form-control bt-runs-n" type="number" min="1" step="1" aria-label=${t('batch.runs_total', 'Number of runs')}
                      title=${t('batch.runs_total_hint', 'Bills on this many runs, then stops')}
                      placeholder=${t('batch.runs_total_ph', 'e.g. 12')} .value=${ln.runs_total || ''}
                      @change=${e => { ln._endMode = 'after'; changeLinePeriod(mid, ln, 'runs_total', e.target.value ? Number(e.target.value) : null); }}>` : ''}
                    ${em === 'after' && ln.runs_total ? html`<span class=${'row-sub bt-runs-used' + ((ln.fired_count || 0) >= ln.runs_total ? ' bt-runs-done' : '')}>
                      ${(ln.fired_count || 0) >= ln.runs_total
                        ? t('batch.runs_ended', 'Ended') + ' — ' + (ln.fired_count || 0) + '/' + ln.runs_total
                        : (ln.fired_count || 0) + '/' + ln.runs_total + ' ' + t('batch.runs_used', 'runs used')}</span>` : ''}
                  </div>`;
                  })()}</td>
          </tr>`;
        })}</tbody>
      </table></div>`}
      <button type="button" class="btn btn-sm btn-outline ed-addline" @click=${() => addLineFor(mid)}>+ ${t('batch.add_line', 'Add line')}</button>
    </div>`;
  }

  const title = () => (tpl.value && tpl.value.name) || t('batch.edit_template', 'Batch template');
  // ── S17 research surfaces (Xero shows the next occurrence, Zoho lists a profile's child
  // invoices + Stripe previews the upcoming invoice) adapted to wave's MANUAL runs: a passive
  // "if you run today" estimate under the head, and the run history with each run's invoices one
  // click away. Both are STABLE self-fetching nodes interpolated once — no reactive slots.
  const estimateNode = document.createElement('div');
  estimateNode.className = 'bt-next-est row-sub';
  api.get(ep.preview(companyId, tplId) + '?invoice_date=' + encodeURIComponent(todayIso()))
    .then(p => {
      if(!p || p.error || !p.member_count) return;
      estimateNode.textContent = t('batch.next_run_est', 'If you run today') + ': '
        + plural(p.member_count, t('batch.invoice_one', 'invoice'), t('batch.invoices_many', 'invoices'))
        + ' · ' + plural(p.line_count, t('batch.line_lc', 'line'), t('batch.lines_lc', 'lines'))
        + ' · ≈ ' + fmtMoney(p.estimated_total || 0, sym(), false);
    }).catch(() => { /* a hint, never an error state */ });

  const runsNode = document.createElement('div');
  runsNode.className = 'card bt-runs';
  runsNode.hidden = true;
  async function toggleRunDocs(r, btn){
    const tr = btn.closest('tr');
    const next = tr.nextElementSibling;
    if(next && next.classList.contains('bt-run-docs')){ next.remove(); return; }
    let docs = [];
    try { docs = (await api.get(ep.runDetail(companyId, r.id))).documents || []; }
    catch { docs = []; }
    const dtr = document.createElement('tr');
    dtr.className = 'bt-run-docs';
    const td = document.createElement('td');
    td.colSpan = tr.children.length;
    td.append(html`<div class="bt-run-doclist">${docs.length ? docs.map(d => html`
      <button type="button" class="btn btn-link bt-run-doc" @click=${() => navigate('/co/' + companyId + '/invoices/' + d.id)}>
        ${d.document_no} · ${d.contact_name || ''} · ${fmtMoney(d.total || 0, d.currency_symbol || d.currency_code || sym(), false)}</button>`)
      : html`<span class="muted">${t('batch.run_no_docs', 'No invoices on this run.')}</span>`}</div>`);
    dtr.append(td);
    tr.after(dtr);
  }
  function paintRuns(rows){
    if(!rows || !rows.length){ runsNode.replaceChildren(); runsNode.hidden = true; return; }
    runsNode.hidden = false;
    runsNode.replaceChildren(html`<h3>${t('batch.prev_runs', 'Previous runs')} <span class="grid-count">${rows.length}</span></h3>
      <div class="tbl-wrap"><table class="list bt-runs-tbl">
        <thead><tr><th>${t('batch.run_col', 'Run')}</th><th>${t('batch.run_date_col', 'Invoice date')}</th>
          <th class="ta-r">${t('batch.run_docs_col', 'Invoices')}</th><th class="ta-r">${t('doc.total', 'Total')}</th>
          <th>${t('doc.col_status', 'Status')}</th><th></th></tr></thead>
        <tbody>${rows.map(r => html`<tr>
          <td class="mono">#${r.id}</td>
          <td class="mono">${formatDate(r.run_date, (ref.value && ref.value.date_format) || undefined)}</td>
          <td class="ta-r num">${r.documents_created || 0}</td>
          <td class="ta-r num mono">${fmtMoney(r.total || 0, sym(), false)}</td>
          <td>${r.status || ''}${r.error_message ? html`<small class="cell-sub">${r.error_message}</small>` : ''}</td>
          <td class="ta-r"><button type="button" class="btn sm" @click=${e => toggleRunDocs(r, e.target)}>${t('batch.view_invoices', 'View invoices')}</button></td>
        </tr>`)}</tbody></table></div>`);
  }
  api.get(ep.runs(companyId, tplId)).then(r => paintRuns((r && r.data) || [])).catch(() => {});

  const content = html`<div class="page page-wide doc-page">
    <div class="page-head with-action"><div>
      ${backChip({ path: '/co/' + companyId + '/batches', label: t('nav.batches', 'Batch Invoicing') })}
      <h1 class="page-title">${() => title()}</h1></div>
      <button class="btn btn-primary" @click=${() => navigate('/co/' + companyId + '/batches/' + tplId + '/run')}>${t('batch.run_batch', 'Run batch')} ${icon('chevron-right', {size: 14})}</button>
    </div>
    ${estimateNode}
    ${() => notice.value ? html`<div class="alert alert-ok mb-2">${notice.value}</div>` : ''}
    ${() => loading.value ? html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>` : ''}
    ${() => err.value ? html`<div class="muted-block">${err.value}</div>` : ''}
    <div class="card">${detailsFields}${notesPreview}</div>
    <div class="bt-editor-cols">${membersCard}</div>
    ${runsNode}
  </div>`;
  return tenantShell(companyId, t('nav.batches', 'Batch Invoicing'), content);
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// RUN — invoice/debit dates, notes override (prefilled from preview), member checklist w/
// exclude + ad-hoc lines + Keep, estimated total, execute-with-confirm, completion summary.
// ════════════════════════════════════════════════════════════════════════════════════════════
function renderRun(companyId, tplId){
  const loading = signal(true), err = signal('');
  const ref = signal(null), tpl = signal(null), members = signal([]);
  const fInvoiceDate = signal(todayIso()), fDebitDate = signal(''), fNotesOverride = signal('');
  const excludes = signal(new Set());
  const adhoc = signal([]);            // committed ad-hoc lines: {member_id,description,qty,unit_price,keep}
  const adhocDraft = signal({});       // per-member in-progress ad-hoc draft, mutated in place
  const preview = signal(null), previewBusy = signal(false);
  const confirmRun = signal(false), running = signal(false), runErr = signal('');
  const runMode = signal('post');   // 'post' = post to the ledger now · 'draft' = create drafts to review (Xero/Stripe pattern)
  const summary = signal(null);
  let previewSupported = true, notesTouched = false, previewTimer = null;

  async function loadRef(){
    try { ref.value = await api.get(ep.reference(companyId)); if(ref.value) configureSleekDate(ref.value); }
    catch { /* non-fatal */ }
  }
  async function loadPreview(){
    if(!previewSupported) return;
    previewBusy.value = true;
    try {
      const r = await api.get(ep.preview(companyId, tplId) + '?invoice_date=' + encodeURIComponent(fInvoiceDate.value));
      preview.value = r;
      if(!notesTouched) fNotesOverride.value = r && r.notes_resolved != null ? r.notes_resolved : resolveTokens((tpl.value && tpl.value.notes) || '', fInvoiceDate.value);
    } catch {
      previewSupported = false; preview.value = null;
      if(!notesTouched) fNotesOverride.value = resolveTokens((tpl.value && tpl.value.notes) || '', fInvoiceDate.value);
    }
    previewBusy.value = false;
  }
  function onInvoiceDateChange(val){
    fInvoiceDate.value = val || todayIso();
    clearTimeout(previewTimer);
    previewTimer = setTimeout(loadPreview, 250);
  }
  (async () => {
    await loadRef();
    try {
      const { tplData, memberList } = await fetchTemplate(companyId, tplId);
      tpl.value = tplData; members.value = memberList;
      // paused members are pre-excluded by default (see final report for this judgment call)
      excludes.value = new Set(memberList.filter(m => m.paused).map(m => m.id));
      err.value = '';
    } catch(e){ err.value = (e.data && e.data.error) || t('batch.err_load_one', 'Could not load this template'); }
    loading.value = false;
    if(!err.value) await loadPreview();
  })();

  const sym = () => (ref.value && ref.value.currency_symbol) || baseCurrencySymbol();

  function toggleExclude(mid, exclude){
    const s = new Set(excludes.value);
    exclude ? s.add(mid) : s.delete(mid);
    excludes.value = s;
  }
  function getDraft(mid){
    if(!adhocDraft.value[mid]) adhocDraft.value[mid] = { description: '', qty: 1, unit_price: 0, keep: false };
    return adhocDraft.value[mid];
  }
  function adhocDraftInput(mid, key, val){ getDraft(mid)[key] = val; }
  function addAdhocLine(mid){
    const d = getDraft(mid);
    if(!String(d.description || '').trim()) return;
    adhoc.value = [...adhoc.value, { member_id: mid, description: d.description.trim(),
      qty: Number(d.qty) || 1, unit_price: Number(d.unit_price) || 0, keep: !!d.keep }];
    adhocDraft.value[mid] = { description: '', qty: 1, unit_price: 0, keep: false };
  }
  function removeAdhocLine(idx){ adhoc.value = adhoc.value.filter((_, i) => i !== idx); }

  async function executeRun(){
    running.value = true; runErr.value = '';
    const body = {
      invoice_date: fInvoiceDate.value, debit_order_date: fDebitDate.value || null,
      notes_override: fNotesOverride.value.trim() || null, excludes: [...excludes.value],
      // Post now (numbers + ledger) or create drafts to review first (Xero/Stripe pattern). The API
      // supports both via `post`; the run screen offers both buttons (owner 2026-09-09).
      post: runMode.value === 'post',
      adhoc: adhoc.value.map(a => ({ member_id: a.member_id, description: a.description, qty: a.qty, unit_price: a.unit_price, keep: !!a.keep })),
    };
    try {
      const res = await api.post(ep.run(companyId, tplId), body);
      // The run response carries counts only ({documents_created, total, run_id, errors[.contact]});
      // the created-document list lives on the run-detail endpoint. The old code read invoice_count/
      // total_amount/invoices — keys that never existed — so the summary showed 0 / R 0.00 even for
      // a successful run (owner sweep 2026-09-05).
      let documents = [];
      try { documents = (await api.get(ep.runDetail(companyId, res.run_id))).documents || []; }
      catch { /* the counts alone still render */ }
      summary.value = { ...res, documents, mode: runMode.value };
      confirmRun.value = false;
    } catch(e){ runErr.value = (e.data && e.data.error) || t('batch.run_failed', 'Could not run this batch'); }
    running.value = false;
  }
  function downloadDebitCsv(runId){
    if(!runId) return;
    // The export endpoints are auth-gated like every batch route — a plain window.open sends no
    // bearer and 401s, so go through api.download (fetch with auth → blob → programmatic save).
    api.download(ep.runCsv(companyId, runId), `debit-order-run-${runId}.csv`).catch(() => {});
  }
  function downloadInvoicesPdf(runId){
    if(!runId) return;
    api.download(ep.runPdf(companyId, runId), `batch-run-${runId}-invoices.pdf`).catch(() => {});
  }

  // How many invoices the run will ACTUALLY create: an included (not excluded/paused) member with at
  // least one due line OR an ad-hoc line. The API skips members with zero lines (batch_service marks
  // them `skipped`), so the old `members − excludes` over-counted the "N invoices" pill and the
  // confirm ("This will post N…"). Uses the same isLineDueThisRun the checklist shows (owner 2026-09-09).
  function memberWillBill(m, ex, adh, invDate){
    if(ex.has(m.id) || m.paused) return false;
    if((m.lines || []).some(l => isLineDueThisRun(l, invDate))) return true;
    return adh.some(a => a.member_id === m.id);
  }
  function billableCount(){
    const ex = excludes.value, adh = adhoc.value, invDate = fInvoiceDate.value;
    return members.value.filter(m => memberWillBill(m, ex, adh, invDate)).length;
  }
  // A preview-before-commit tally for the run summary bar (Stripe/Zoho/Xero all show one): invoices,
  // total lines and the estimated total, computed client-side so it tracks the current excludes and
  // ad-hoc lines live (the server /preview total ignores those). owner 2026-09-09.
  function runTotals(){
    const ex = excludes.value, adh = adhoc.value, invDate = fInvoiceDate.value;
    let invoices = 0, lines = 0, total = 0;
    members.value.forEach(m => {
      if(!memberWillBill(m, ex, adh, invDate)) return;
      invoices++;
      const due = (m.lines || []).filter(l => isLineDueThisRun(l, invDate));
      const mAdhoc = adh.filter(a => a.member_id === m.id);
      lines += due.length + mAdhoc.length;
      due.forEach(l => { total += (Number(l.qty) || 0) * (Number(l.unit_price) || 0); });
      mAdhoc.forEach(a => { total += (Number(a.qty) || 0) * (Number(a.unit_price) || 0); });
    });
    return { invoices, lines, total };
  }

  // ── render: runForm / summaryPanel are independent top-level slots, each gated on
  // summary.value at their OWN top so neither nests inside the other. ──────────────────────────
  function renderMemberChecklist(ms, ex, adh, drafts, invDate){
    return html`<div class="bt-members">${ms.map(m => {
      const isExcluded = ex.has(m.id) || m.paused;
      const draft = drafts[m.id] || { description: '', qty: 1, unit_price: 0, keep: false };
      const dueLines = (m.lines || []).filter(l => isLineDueThisRun(l, invDate));
      const memberAdhoc = adh.map((a, i) => ({ a, i })).filter(x => x.a.member_id === m.id);
      return html`<div class=${'card bt-member' + (isExcluded ? ' bt-excluded' : '') + (m.paused ? ' bt-paused' : '')}>
        <div class="bt-member-head">
          <label class="bt-member-name">
            <input type="checkbox" .checked=${!isExcluded} ?disabled=${m.paused} title=${m.paused ? t('batch.paused_hint', 'Paused in the template — resume it in the editor to include this run.') : ''}
              @change=${e => toggleExclude(m.id, !e.target.checked)}>
            ${m.contact_name}${m.paused ? html`<span class="bt-pausetag">${t('batch.paused', 'Paused')}</span>` : ''}
          </label>
          <div class="bt-member-total">${plural(dueLines.length + memberAdhoc.length, t('batch.line_lc', 'line'), t('batch.lines_lc', 'lines'))}</div>
        </div>
        ${!isExcluded && (dueLines.length + memberAdhoc.length) === 0
          ? html`<div class="row-sub bt-wontbill">${t('batch.wont_bill', 'No due lines — this member won’t be invoiced')}</div>` : ''}
        <div class="bt-lines">
          ${dueLines.map(l => html`<div class="bt-line">
            <span class="bt-line-desc">${l.description}</span>
            <span class="bt-line-qty">×${l.qty}</span>
            <span class="bt-line-amt">${fmtMoney(Number(l.qty) * Number(l.unit_price), sym(), false)}</span>
          </div>`)}
          ${memberAdhoc.map(({ a, i }) => html`<div class="bt-line bt-run-adhoc">
            <span class="bt-adhoc-tag">${t('batch.adhoc', 'Ad-hoc')}</span>
            <span class="bt-line-desc">${a.description}</span>
            <span class="bt-line-qty">×${a.qty}</span>
            <span class="bt-line-amt">${fmtMoney(a.qty * a.unit_price, sym(), false)}</span>
            <button type="button" class="edln-del" title=${t('doc.remove_line', 'Remove line')} @click=${() => removeAdhocLine(i)}>✕</button>
          </div>`)}
        </div>
        <div class="bt-run-addadhoc">
          <input class="form-control bt-run-desc" placeholder=${t('batch.adhoc_desc_ph', 'e.g. Callout fee')}
            .value=${draft.description} @input=${e => adhocDraftInput(m.id, 'description', e.target.value)}>
          <input class="form-control bt-edit-num" type="number" step="0.01" placeholder="e.g. 1"
            .value=${draft.qty} @input=${e => adhocDraftInput(m.id, 'qty', e.target.value)}>
          <input class="form-control bt-edit-num" type="number" step="0.01" placeholder="e.g. 250.00"
            .value=${draft.unit_price} @input=${e => adhocDraftInput(m.id, 'unit_price', e.target.value)}>
          <label class="bt-keep"><input type="checkbox" .checked=${draft.keep}
            @change=${e => adhocDraftInput(m.id, 'keep', e.target.checked)}> ${t('batch.keep', 'Keep')}</label>
          <button type="button" class="btn btn-sm btn-outline" @click=${() => addAdhocLine(m.id)}>+ ${t('batch.add', 'Add')}</button>
        </div>
      </div>`;
    })}</div>`;
  }

  const runForm = () => {
    if(summary.value) return '';
    const ms = members.value, ex = excludes.value, adh = adhoc.value, drafts = adhocDraft.value;
    const isRunning = running.value, invDate = fInvoiceDate.value;
    const rt = runTotals();
    return html`<div>
      <div class="card bt-runpage"><div class="bt-runhead">
        <div class="bt-run-row">
          <label class="ed-f"><span>${t('batch.invoice_date', 'Invoice date')}</span>
            <sleek-date .value=${fInvoiceDate} @change=${e => onInvoiceDateChange(e.target.value)}></sleek-date></label>
          <label class="ed-f"><span>${t('batch.debit_date', 'Debit-order date')}</span>
            <sleek-date data-free="true" .value=${fDebitDate} @change=${e => { fDebitDate.value = e.target.value; }}></sleek-date></label>
        </div>
        <div class="bt-run-summary">
          <span class="qi-pill qi-pill-info">${plural(rt.invoices, t('batch.invoice_lc', 'invoice'), t('batch.invoices_lc', 'invoices'))}</span>
          <span class="qi-pill bt-run-lines">${plural(rt.lines, t('batch.line_lc', 'line'), t('batch.lines_lc', 'lines'))}</span>
          <span class="qi-pill bt-run-total">≈ ${fmtMoney(rt.total, sym(), false)}</span>
        </div>
        <label class="ed-notes mt-2 bt-run-notes"><span>${t('batch.notes_override', 'Notes for this run')}</span>
          <textarea class="form-control" rows="2" .value=${fNotesOverride}
            placeholder=${t('batch.notes_override_ph', 'e.g. Thank you for your business in March')}
            @input=${e => { fNotesOverride.value = e.target.value; notesTouched = true; }}></textarea></label>
      </div></div>
      ${!ms.length ? html`<div class="bt-empty muted-block">${t('batch.no_members', 'No members yet — add some in the template editor first.')}</div>`
                    : renderMemberChecklist(ms, ex, adh, drafts, invDate)}
      <div class="edit-actions bt-run-actions">
        <button type="button" class="btn btn-outline" @click=${() => navigate('/co/' + companyId + '/batches/' + tplId)}>${t('common.cancel', 'Cancel')}</button>
        <button type="button" class="btn btn-outline bt-btn-draft" ?disabled=${isRunning || rt.invoices <= 0}
          title=${t('batch.create_drafts_hint', 'Create the invoices as drafts so you can review them before posting')}
          @click=${() => { runMode.value = 'draft'; confirmRun.value = true; }}>${t('batch.create_drafts', 'Create drafts')}</button>
        <button type="button" class="btn btn-primary" ?disabled=${isRunning || rt.invoices <= 0}
          @click=${() => { runMode.value = 'post'; confirmRun.value = true; }}>
          ${isRunning ? t('batch.running', 'Posting…') : t('batch.post_now', 'Post now')}</button>
      </div>
    </div>`;
  };

  const summaryPanel = () => {
    const s = summary.value; if(!s) return '';
    const invs = s.documents || [], errs = s.errors || [];   // run-detail documents + execute_run's errors[{contact,error}]
    const skipped = s.skipped || [], dups = s.duplicates || [], isDraft = s.mode === 'draft';
    return html`<div class="card bt-complete">
      <div class="bt-complete-title">✓ ${isDraft ? t('batch.drafts_complete', 'Draft invoices created') : t('batch.run_complete', 'Batch run complete')}</div>
      <div class="bt-complete-grid">
        <div class="bt-complete-row"><span>${isDraft ? t('batch.drafts_created', 'Drafts created') : t('batch.posted', 'Invoices posted')}</span><span class="fw-bold">${s.documents_created != null ? s.documents_created : invs.length}</span></div>
        <div class="bt-complete-row"><span>${t('doc.total', 'Total')}</span><span class="fw-bold mono">${fmtMoney(s.total, sym(), false)}</span></div>
      </div>
      ${invs.length ? html`<div class="bt-lines mt-2">${invs.map(inv => html`<div class="bt-line">
          <span class="bt-link" @click=${() => navigate('/co/' + companyId + '/invoices/' + inv.id)}>${inv.document_no}</span>
          <span class="bt-line-desc">${inv.contact_name || ''}</span>
          <span class="bt-line-amt">${fmtMoney(inv.total, sym(), false)}</span>
        </div>`)}</div>` : ''}
      ${skipped.length ? html`<div class="row-sub mt-2">${t('batch.skipped_note', 'Skipped (no due lines)')}: ${skipped.join(', ')}</div>` : ''}
      ${dups.length ? html`<div class="row-sub">${t('batch.duplicates_note', 'Already invoiced this date')}: ${dups.join(', ')}</div>` : ''}
      ${errs.length ? html`<div class="bt-complete-errs">${errs.map(er => html`<div class="row-err">${er.contact || er.member}: ${er.error}</div>`)}</div>` : ''}
      <div class="bt-complete-actions bt-complete-actions-right">
        <button type="button" class="btn btn-outline" @click=${() => downloadInvoicesPdf(s.run_id)}>⇩ ${t('batch.download_pdf', 'Download invoices (PDF)')}</button>
        <button type="button" class="btn btn-outline" @click=${() => downloadDebitCsv(s.run_id)}>⇩ ${t('batch.download_csv', 'Download debit-order CSV')}</button>
        <button type="button" class="btn btn-primary" @click=${() => navigate('/co/' + companyId + '/batches')}>${t('batch.back_to_batches', 'Back to batches')}</button>
      </div>
    </div>`;
  };

  const confirmModal = () => {
    if(!confirmRun.value) return '';
    const rt = runTotals(), isRunning = running.value, rErr = runErr.value, isDraft = runMode.value === 'draft';
    return html`<div class="modal-overlay" @click=${() => { if(!isRunning) confirmRun.value = false; }}>
      <div class="modal-card" @click=${e => e.stopPropagation()}>
        <div class="modal-head"><h3>${isDraft ? t('batch.confirm_draft_title', 'Create draft invoices?') : t('batch.confirm_title', 'Post this batch?')}</h3>
          <button type="button" class="modal-x" ?disabled=${isRunning} @click=${() => { confirmRun.value = false; }}>✕</button></div>
        <div class="form-note">${isDraft ? t('batch.confirm_draft', 'This will create') : t('batch.confirm_body', 'This will post')} ${rt.invoices} ${isDraft ? t('batch.draft_prefix', 'draft ') : ''}${rt.invoices === 1 ? t('batch.invoice_lc', 'invoice') : t('batch.invoices_lc', 'invoices')} — ${t('batch.continue_q', 'continue?')}</div>
        ${rErr ? html`<div class="row-err mb-2">${rErr}</div>` : ''}
        <div class="form-actions">
          <button type="button" class="btn btn-outline" ?disabled=${isRunning} @click=${() => { confirmRun.value = false; }}>${t('common.no', 'No')}</button>
          <button type="button" class="btn btn-primary" ?disabled=${isRunning} @click=${executeRun}>${isRunning ? '…' : t('common.yes', 'Yes')}</button>
        </div>
      </div>
    </div>`;
  };

  const title = () => t('batch.run_batch', 'Run batch') + ': ' + ((tpl.value && tpl.value.name) || '');
  const content = html`<div class="page doc-page bt-runpage">
    <div class="page-head"><div>
      ${backChip({ path: '/co/' + companyId + '/batches/' + tplId, label: t('batch.edit_template', 'Batch template') })}
      <h1 class="page-title">${() => title()}</h1></div></div>
    ${() => loading.value ? html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>` : ''}
    ${() => err.value ? html`<div class="muted-block">${err.value}</div>` : ''}
    ${runForm}
    ${summaryPanel}
  </div>
  ${confirmModal}`;
  return tenantShell(companyId, t('nav.batches', 'Batch Invoicing'), content);
}
